"use client";

import Image from "next/image";
import { m } from "framer-motion";
import { Gift, Handshake } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { LEADERSHIP, PHONE_DISPLAY, PHONE_TEL } from "./landing-shared";

/* ─────────────────────────────────────────────────────────────
 * Kundenvorteil für PCBoot-Bestandskunden. Bewusst OHNE Frist und ohne
 * Countdown: erfundene Verknappung ist nach UWG (Anhang Nr. 7) verboten
 * und wirkt bei Geschäftskunden ohnehin unseriös. Die konkrete Höhe des
 * Rabatts nennt die Kampagne / das Gespräch – hier nichts erfinden.
 * ──────────────────────────────────────────────────────────── */
export function PcbootAdvantageCard({ compact = false }: { compact?: boolean }) {
  return (
    <div
      className={`rounded-2xl border ${compact ? "p-5" : "p-6"}`}
      style={{ borderColor: "var(--fi-brand-fg)", backgroundColor: "var(--fi-brand-bg)" }}
    >
      <div className="flex items-start gap-3">
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
          style={{ backgroundColor: "var(--card-bg)", color: "var(--fi-brand-fg)" }}
        >
          <Gift className="h-5 w-5" />
        </span>
        <div>
          <p className="text-base font-semibold" style={{ color: "var(--text)" }}>
            Ihr Vorteil als PCBoot-Kunde
          </p>
          <p className="mt-1 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            Sie lernen alle Leistungen vorab kennen und erhalten einen Kundenrabatt, den wir
            ausschließlich bestehenden Geschäftspartnern von PCBoot einräumen. Die Details
            besprechen wir persönlich – nennen Sie im Gespräch oder im Formular einfach das
            Stichwort <strong style={{ color: "var(--text)" }}>„PCBoot“</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Brücke PCBoot → DMD Studio: dieselben Menschen, dasselbe Haus in Salz.
 * Vertrauen wird nicht neu aufgebaut, sondern übertragen. PCBoot wird
 * bewusst NICHT als Referenzprojekt genannt.
 * ──────────────────────────────────────────────────────────── */
export function PcbootBridge() {
  return (
    <section className="pb-6 pt-2">
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <m.div
          className="mx-auto grid max-w-4xl items-center gap-6 rounded-2xl border p-6 sm:p-8 lg:grid-cols-5"
          style={{ borderColor: "var(--border)", backgroundColor: "var(--card-bg)", boxShadow: "var(--card-shadow-md)" }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
        >
          <div className="flex items-center justify-center gap-4 lg:col-span-2">
            <span className="flex h-16 w-32 items-center justify-center rounded-xl bg-white p-2 ring-1 ring-black/5">
              <Image
                src="/images/logos/pcboot.webp"
                alt="PCBoot GmbH & Co. KG"
                width={112}
                height={40}
                className="h-auto max-h-10 w-auto object-contain"
              />
            </span>
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
              style={{ backgroundColor: "var(--fi-brand-bg)", color: "var(--fi-brand-fg)" }}
            >
              <Handshake className="h-4.5 w-4.5" />
            </span>
            <span className="flex h-16 w-32 items-center justify-center rounded-xl p-2" style={{ backgroundColor: "var(--bg-secondary)" }}>
              <BrandLogo height={32} className="h-8 w-auto" />
            </span>
          </div>
          <div className="lg:col-span-3">
            <p className="text-sm font-semibold text-brand-500">Neu von den Menschen hinter PCBoot</p>
            <h2
              className="mt-1.5 text-xl font-semibold tracking-tight sm:text-2xl"
              style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
            >
              Sie kennen uns bereits – jetzt auch für Website, Software und Marketing.
            </h2>
            <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
              Sebastian Erhard und Pascal Reininger, die Sie von PCBoot kennen, haben gemeinsam mit
              Tobias Fischer die DMD Studio GmbH gegründet – im selben Haus in Salz, mit derselben
              Verlässlichkeit. Was Kunden bei PCBoot immer wieder nachgefragt haben, gibt es jetzt
              aus einer Hand.
            </p>
            {/* Die bekannten Gesichter direkt hier – nicht erst weiter unten. */}
            <div className="mt-4 flex flex-wrap gap-5">
              {LEADERSHIP.filter((m) => m.pcboot).map((m) => (
                <div key={m.name} className="flex items-center gap-3">
                  <Image
                    src={m.image}
                    alt={m.name}
                    width={52}
                    height={52}
                    className="h-13 w-13 rounded-full object-cover"
                    style={{ border: "2px solid var(--card-bg)", boxShadow: "var(--card-shadow-md)" }}
                  />
                  <div>
                    <p className="text-sm font-semibold leading-tight" style={{ color: "var(--text)" }}>
                      {m.name}
                    </p>
                    <p className="text-xs" style={{ color: "var(--text-tertiary)" }}>
                      {m.title} · Ihnen von PCBoot bekannt
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </m.div>
        <p className="mx-auto mt-4 max-w-4xl text-center text-sm" style={{ color: "var(--text-secondary)" }}>
          Sie haben Fragen zu dieser Seite? Rufen Sie uns direkt an:{" "}
          <a href={`tel:${PHONE_TEL}`} className="font-semibold text-brand-500 hover:underline">
            {PHONE_DISPLAY}
          </a>
        </p>
      </div>
    </section>
  );
}
